import { z } from 'zod';
import { FillSchema, StrokeSchema, TextStyleSchema } from './style';

/**
 * Display primitives in object-local coordinates (0..w, 0..h).
 * Core shapes and module objects in render mode A both produce these (§7.3),
 * so renderers, hit-testing and SVG export share one path.
 */
const n = z.number().finite();

/**
 * API 2.2: a clickable area of a module object (render mode A, type capability `actions`): a click
 * on the primitive sends `object.action` with this id and data to the module instead of selecting.
 */
export const PrimitiveActionSchema = z.object({
  id: z.string().regex(/^[\w.:-]{1,64}$/),
  data: z.union([z.string().max(500), z.number(), z.boolean(), z.null()]).optional(),
});
export type PrimitiveAction = z.infer<typeof PrimitiveActionSchema>;

export const ShapePrimitiveSchema = z.object({
  k: z.literal('shape'),
  shape: z.enum(['rect', 'ellipse', 'triangle', 'diamond']),
  x: n,
  y: n,
  w: n.min(0),
  h: n.min(0),
  radius: n.min(0).optional(),
  fill: FillSchema,
  stroke: StrokeSchema.nullable(),
  opacity: z.number().min(0).max(1).optional(),
  action: PrimitiveActionSchema.optional(),
});

export const PathPrimitiveSchema = z.object({
  k: z.literal('path'),
  /** Flat [x0, y0, x1, y1, ...]. */
  points: z.array(n).min(4).max(200_000),
  closed: z.boolean(),
  /** Holes of a closed path (flat rings, even-odd): results of shape operations such as subtract. */
  holes: z.array(z.array(n).min(6).max(200_000)).max(2_000).optional(),
  fill: FillSchema,
  stroke: StrokeSchema.nullable(),
  arrowStart: z.boolean().optional(),
  arrowEnd: z.boolean().optional(),
  /** Round joins and caps (freehand strokes). */
  round: z.boolean().optional(),
  /** Full width at each point (same count as `points` / 2). Stylus pressure. */
  widths: z.array(n).max(100_000).optional(),
  /** Highlighter: multiply on a light canvas, screen on a dark one. */
  blend: z.literal('highlight').optional(),
  opacity: z.number().min(0).max(1).optional(),
  action: PrimitiveActionSchema.optional(),
});

export const TextPrimitiveSchema = z.object({
  k: z.literal('text'),
  x: n,
  y: n,
  w: n.min(0),
  h: n.min(0),
  text: z.string().max(100_000),
  style: TextStyleSchema,
  opacity: z.number().min(0).max(1).optional(),
  action: PrimitiveActionSchema.optional(),
});

export const ImagePrimitiveSchema = z.object({
  k: z.literal('image'),
  x: n,
  y: n,
  w: n.min(0),
  h: n.min(0),
  /** `att:<sha256>` attachment reference. */
  src: z.string().max(200),
  opacity: z.number().min(0).max(1).optional(),
  action: PrimitiveActionSchema.optional(),
});

export const PrimitiveSchema = z.discriminatedUnion('k', [
  ShapePrimitiveSchema,
  PathPrimitiveSchema,
  TextPrimitiveSchema,
  ImagePrimitiveSchema,
]);

export type ShapePrimitive = z.infer<typeof ShapePrimitiveSchema>;
export type PathPrimitive = z.infer<typeof PathPrimitiveSchema>;
export type TextPrimitive = z.infer<typeof TextPrimitiveSchema>;
export type ImagePrimitive = z.infer<typeof ImagePrimitiveSchema>;

/** Raster produced by a module in mode B (internal; not part of module-visible schema). */
export interface RasterPrimitive {
  k: 'raster';
  x: number;
  y: number;
  w: number;
  h: number;
  /** Cache key the renderer uses to find the bitmap. */
  key: string;
  bitmap: ImageBitmap | null;
  opacity?: number;
}

export type Primitive = z.infer<typeof PrimitiveSchema> | RasterPrimitive;

export const PrimitiveListSchema = z.array(PrimitiveSchema).max(50_000);
